import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null); // Track user role
  const [adminRole, setAdminRole] = useState(null); // Track admin role

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    verifyOnInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyWithToken = async (token) => {
    if (!token) throw new Error('No token provided');
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const response = await axios.get(`${API_BASE_URL}/api/auth/verify`);
    return response.data.user;
  };

  const applyVerifiedUser = (userData, token, source = 'user') => {
    setUser(userData);
    // 1) Superadmin tokens (legacy or via admin login)
    if (userData?.is_superadmin) {
      setUserRole('admin');
      setAdminRole('superadmin');
      // Ensure admin_token is set and mirror into user token to keep all API calls working
      localStorage.setItem('admin_token', token);
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return;
    }
    // 2) Admin users with admin_role
    const role = userData?.role || 'user';
    const adminRoleValue = userData?.admin_role || null;
    setUserRole(role);
    setAdminRole(adminRoleValue);
    if (role === 'admin' && adminRoleValue) {
      localStorage.setItem('admin_token', token);
    }
    // Keep current token for axios
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const verifyOnInit = async () => {
    setLoading(true);
    const storedToken = localStorage.getItem('token');
    const adminToken = localStorage.getItem('admin_token');

    // 1) Try regular user token first
    if (storedToken) {
      try {
        const userData = await verifyWithToken(storedToken);
        applyVerifiedUser(userData, storedToken, 'user');
        setLoading(false);
        return;
      } catch (error) {
        // Only clear the regular token; do not clear admin_token here
        console.warn('User token verification failed on init:', error?.response?.status || error?.message);
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        // Continue to try admin token
      }
    }

    // 2) If user token failed (or absent), try admin token as fallback (prevents logout on admin refresh)
    if (adminToken) {
      try {
        const adminUser = await verifyWithToken(adminToken);
        applyVerifiedUser(adminUser, adminToken, 'admin');
        setLoading(false);
        return;
      } catch (error) {
        console.warn('Admin token verification failed on init:', error?.response?.status || error?.message);
        // If admin token is invalid, clear it to force re-auth
        localStorage.removeItem('admin_token');
        delete axios.defaults.headers.common['Authorization'];
      }
    }

    // 3) No valid session
    setUser(null);
    setUserRole(null);
    setAdminRole(null);
    setLoading(false);
  };

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/verify`);
      const userData = response.data.user;
      applyVerifiedUser(userData, localStorage.getItem('token') || localStorage.getItem('admin_token') || '');
    } catch (error) {
      console.error('Token verification failed:', error);
      // Only clear regular token here; admin_token is handled separately by verifyOnInit fallback
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setUserRole(null);
      setAdminRole(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password,
      });
      
      const { access_token, user } = response.data;
      console.log('🔍 DEBUG: Login response user data:', user);
      
      localStorage.setItem('token', access_token);
      
      // For admin users, also store as admin_token for admin functions
      const role = user?.role || 'user';
      const adminRoleValue = user?.admin_role || null;
      
      console.log('🔍 DEBUG: User role:', role);
      console.log('🔍 DEBUG: Admin role:', adminRoleValue);
      
      if (role === 'admin' && adminRoleValue) {
        console.log('🔍 DEBUG: Setting admin_token for admin user');
        localStorage.setItem('admin_token', access_token);
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      setUserRole(role);
      setAdminRole(adminRoleValue);
      
      console.log('🔍 DEBUG: Updated auth context state - userRole:', role, 'adminRole:', adminRoleValue);
      
      return { success: true, role, adminRole: adminRoleValue, isSuperadmin: !!user?.is_superadmin };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (email, password, fullName, companyName, phoneNumber) => {
    try {
      console.log('Attempting registration with API URL:', API_BASE_URL);
      const response = await axios.post(`${API_BASE_URL}/api/auth/register`, {
        email,
        password,
        full_name: fullName,
        company_name: companyName,
        phone_number: phoneNumber,
      });
      
      // Check if response indicates verification is required
      if (response.data.verification_required) {
        return { 
          success: true, 
          verification_required: true,
          email: response.data.email,
          message: response.data.message
        };
      }
      
      // Legacy response format (if somehow still activated immediately)
      if (response.data.access_token) {
        const { access_token, user } = response.data;
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setUser(user);
        return { success: true };
      }
      
      // Fallback - should not happen with new flow
      return { 
        success: true, 
        verification_required: true,
        email: email,
        message: 'Registration successful! Please check your email to verify your account.'
      };
      
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        return { 
          success: false, 
          error: 'Cannot connect to server. Please check your internet connection and try again.' 
        };
      }
      
      if (error.response?.status === 400) {
        const detail = error.response?.data?.detail;
        if (detail?.includes('already registered and verified')) {
          return { 
            success: false, 
            error: 'An account with this email already exists and is verified. Please try logging in.' 
          };
        }
        if (detail?.includes('Account exists but not verified')) {
          return { 
            success: true, 
            verification_required: true,
            email: email,
            message: 'Account exists but not verified. New verification email sent.'
          };
        }
      }
      
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Registration failed. Please try again.' 
      };
    }
  };

  const logout = () => {
    try {
      // Clear all persisted auth state
      localStorage.removeItem('token');
      localStorage.removeItem('admin_token');
      sessionStorage.removeItem('loginAsToken');
      sessionStorage.removeItem('loginAsUserData');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setUserRole(null);
      setAdminRole(null);
      // Hard redirect to login to avoid rendering protected layout with stale state
      window.location.href = '/login';
    } catch (e) {
      window.location.href = '/login';
    }
  };

  const updateUser = useCallback((userData) => {
    setUser(userData);
    // Update role information when user is updated
    const role = userData?.role || 'user';
    const adminRoleValue = userData?.admin_role || null;
    // Also handle superadmin user model from backend verify
    if (userData?.is_superadmin) {
      setUserRole('admin');
      setAdminRole('superadmin');
      return;
    }
    setUserRole(role);
    setAdminRole(adminRoleValue);
  }, []);

  // Admin login for legacy superadmin accounts
  const adminLogin = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/superadmin/login`, {
        username,
        password,
      });
      
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('admin_token', access_token);
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      setUserRole('admin');
      setAdminRole('superadmin');
      
      return { success: true, admin: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Admin login failed' 
      };
    }
  };
  const isAdmin = () => {
    const result = userRole === 'admin';
    console.log('🔍 DEBUG: isAdmin() - userRole:', userRole, 'result:', result);
    return result;
  };
  
  const isSuperAdmin = () => {
    const result = adminRole === 'superadmin';
    console.log('🔍 DEBUG: isSuperAdmin() - adminRole:', adminRole, 'result:', result);
    return result;
  };
  
  const isUserManager = () => {
    const result = adminRole === 'user_manager';
    console.log('🔍 DEBUG: isUserManager() - adminRole:', adminRole, 'result:', result);
    return result;
  };
  
  const hasAnyAdminRole = () => {
    const result = isAdmin() && (isSuperAdmin() || isUserManager());
    console.log('🔍 DEBUG: hasAnyAdminRole() - isAdmin:', isAdmin(), 'isSuperAdmin:', isSuperAdmin(), 'isUserManager:', isUserManager(), 'result:', result);
    return result;
  };

  const value = {
    user,
    userRole,
    adminRole,
    login,
    adminLogin,
    register,
    logout,
    updateUser,
    loading,
    // Role checking helpers
    isAdmin,
    isSuperAdmin,
    isUserManager,
    hasAnyAdminRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};